import { useEffect, useState, useRef } from "react";
import { Link, Route, Routes, useParams, useNavigate, Navigate } from "react-router-dom";
import { TopBar, NavTabs } from "../components/Layout";
import api, { formatApiError, API } from "../api";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { useAuth } from "../contexts/AuthContext";
import { Users, Layers, CheckCircle2, BarChart3, Plus, ArrowUpRight, ArrowDownRight, Minus, FileText, Download, Upload, Wand2, RotateCcw, Pencil, Trash2, X, BookOpen, Mail, TrendingUp, Scale, Send, Eye, Save, Sparkles, MessageSquare, ChevronDown, ChevronUp, ShieldCheck, AlertTriangle, Mic, Play, Loader2 } from "lucide-react";
import { IntegrityList, IntegrityDetail } from "./admin/IntegrityCenter";
import TokenCostCenter from "./admin/TokenCostCenter";
import ProgrammesAdmin from "./admin/ProgrammesAdmin";
import RubricHandbook from "./admin/RubricHandbook";
import SecurityHolds from "./admin/SecurityHolds";
import CyclesTab from "./admin/CyclesTab";
import EmailsTab from "./admin/EmailsTab";
import ContentTab from "./admin/ContentTab";
import UsersTab from "./admin/UsersTab";
import { ResultsReleaseCard } from "./admin/ReleasePreviewTab";
import {
  RetestConfirmModal,
  SalesCertReportDrawer,
  Results,
  ResultDetail,
  Deliverables,
} from "./admin/ResultsTab";
import {
  PageHeader as SharedPageHeader,
  Loading as SharedLoading,
  Err as SharedErr,
  HeatCell as SharedHeatCell,
  KPI as SharedKPI,
  KpiTile as SharedKpiTile,
} from "./admin/_shared";
import { useRouteTracker, trackClick, trackDownload } from "../hooks/useActivityLogger";

const ALL_TABS = [
  { to: "/admin", label: "Overview", roles: ["admin", "client_lead"] },
  { to: "/admin/cycles", label: "Cycles", roles: ["admin", "client_lead"] },
  { to: "/admin/people", label: "People", roles: ["admin", "client_lead"] },
  { to: "/admin/content", label: "Content Studio", roles: ["admin"] },
  { to: "/admin/programmes", label: "Programmes", roles: ["admin"] },
  { to: "/admin/results", label: "Results", roles: ["admin", "client_lead"] },
  { to: "/admin/integrity", label: "Integrity", roles: ["admin", "client_lead"] },
  { to: "/admin/security-holds", label: "Security Holds", roles: ["admin", "client_lead"] },
  { to: "/admin/token-cost", label: "Token Cost", roles: ["admin"] },
  { to: "/admin/disputes", label: "Disputes", roles: ["admin"] },
  { to: "/admin/performance", label: "Performance", roles: ["admin", "client_lead"] },
  { to: "/admin/emails", label: "Emails", roles: ["admin", "client_lead"] },
  { to: "/admin/deliverables", label: "Deliverables", roles: ["admin"] },
  { to: "/admin/rubric", label: "Rubric", roles: ["admin", "client_lead"] },
  { to: "/admin/system", label: "System", roles: ["admin"] },
  { to: "/admin/demo", label: "Demo", roles: ["admin", "client_lead"] },
];

export default function AdminApp() {
  const { user } = useAuth();
  const role = user?.role || "admin";
  const isClient = role === "client_lead";
  const [uiVis, setUiVis] = useState(null);
  useRouteTracker();
  useEffect(() => {
    api.get("/settings/ui-visibility")
      .then((r) => setUiVis(r.data))
      .catch(() => setUiVis({}));
  }, []);
  // Wait for settings to load before rendering nav so client_lead never sees a flicker of the Integrity tab
  if (uiVis === null) {
    return <div className="min-h-screen flex items-center justify-center text-[#525252] text-sm">Loading workspace…</div>;
  }
  const tabs = ALL_TABS.filter((t) => {
    if (!t.roles.includes(role)) return false;
    if (t.to === "/admin/integrity" && isClient && !uiVis.client_lead_integrity) return false;
    return true;
  });
  return (
    <div className="min-h-screen">
      <TopBar><NavTabs tabs={tabs} /></TopBar>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <Routes>
          <Route index element={<Overview isClient={isClient} />} />
          <Route path="cycles" element={<CyclesTab isClient={isClient} />} />
          <Route path="people" element={<UsersTab isClient={isClient} />} />
          {!isClient && <Route path="content" element={<ContentTab />} />}
          {!isClient && <Route path="programmes" element={<ProgrammesAdmin />} />}
          <Route path="results" element={<Results />} />
          <Route path="results/:assignmentId" element={<ResultDetail />} />
          {(!isClient || uiVis.client_lead_integrity) && <Route path="integrity" element={<IntegrityList />} />}
          {(!isClient || uiVis.client_lead_integrity) && <Route path="integrity/:assignmentId" element={<IntegrityDetail />} />}
          <Route path="security-holds" element={<SecurityHolds />} />
          <Route path="token-cost" element={<TokenCostCenter />} />
          {!isClient && <Route path="disputes" element={<Disputes />} />}
          {!isClient && <Route path="disputes/:sessionId" element={<DisputeDetail />} />}
          <Route path="performance" element={<Performance />} />
          <Route path="emails" element={<EmailsTab />} />
          {!isClient && <Route path="deliverables" element={<Deliverables />} />}
          <Route path="rubric" element={<RubricHandbook />} />
          {!isClient && <Route path="system" element={<SystemReset />} />}
          <Route path="demo" element={<DemoPanel />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}


// Results release control + preview modal moved to admin/ReleasePreviewTab.jsx.

// ---------------- Overview / Dashboard ----------------
function Overview({ isClient = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [working, setWorking] = useState("");

  const load = () =>
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));

  useEffect(() => { load(); }, []);

  const prefill = async () => {
    setWorking("prefill");
    try { await api.post("/admin/demo-prefill"); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setWorking(""); }
  };
  const reset = async () => {
    if (!window.confirm("This will wipe all submissions, role-plays, ratings, and results. Continue?")) return;
    setWorking("reset");
    try { await api.post("/admin/demo-reset"); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setWorking(""); }
  };
  const seedHistory = async () => {
    setWorking("history");
    try {
      const r = await api.post("/admin/demo-seed-history");
      await load();
      alert(`Seeded historical cycle "${r.data.cycle}" with ${r.data.created} prior-cycle results. Longitudinal trend chart will now show movement on every participant report.`);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setWorking(""); }
  };
  const [brief, setBrief] = useState(null);
  const generateBrief = async () => {
    setWorking("brief"); setBrief(null);
    trackClick("ai_executive_brief");
    try {
      const r = await api.post("/admin/executive-brief");
      setBrief(r.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setWorking(""); }
  };

  if (err) return <Err msg={err} />;
  if (!data) return <Loading />;

  const k = data.kpis;
  return (
    <div className="space-y-10 fade-in">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          eyebrow="Q1 2026 PILOT · LIVE"
          title="Sales Capability Control Room"
          subtitle="Aggregate view across all active assessment cycles. Drill down into competencies, regions, and individuals."
        />
        <div className="flex gap-2 flex-wrap">
          <button onClick={generateBrief} disabled={!!working} className="btn btn-primary !text-xs" data-testid="exec-brief-btn" title="Synthesise the cohort into a Sales-Head-ready 200-word brief">
            <Sparkles size={14} /> {working === "brief" ? "Generating…" : "AI Executive Brief"}
          </button>
          {!isClient && (
            <>
              <button onClick={prefill} disabled={!!working} className="btn btn-secondary !text-xs" data-testid="demo-prefill-btn" title="Auto-score all pending assignments with plausible demo data">
                <Wand2 size={14} /> {working === "prefill" ? "Generating…" : "Demo pre-fill"}
              </button>
              <button onClick={seedHistory} disabled={!!working} className="btn btn-secondary !text-xs" data-testid="demo-seed-history-btn" title="Seed a prior Q4 2025 cycle so the longitudinal trend chart has data">
                <TrendingUp size={14} /> {working === "history" ? "Seeding…" : "Seed prior cycle"}
              </button>
              <button onClick={reset} disabled={!!working} className="btn btn-ghost !text-xs" data-testid="demo-reset-btn" title="Wipe all assessment data">
                <RotateCcw size={14} /> {working === "reset" ? "Resetting…" : "Reset"}
              </button>
            </>
          )}
        </div>
      </div>

      {brief && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setBrief(null)} data-testid="exec-brief-modal">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0A0A0A] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#EE2737]">AI EXECUTIVE BRIEF</div>
                <h2 className="font-heading font-black text-lg">For VI Sales Head · {brief.stats.cohort_size} reps</h2>
              </div>
              <button onClick={() => setBrief(null)} className="text-white/70 hover:text-white" data-testid="close-brief"><X size={22} /></button>
            </div>
            <div className="p-7">
              <p className="font-body text-[15px] leading-relaxed text-[#0A0A0A] whitespace-pre-wrap" data-testid="exec-brief-text">{brief.brief}</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="border border-[#10B981]/30 bg-[#D1FAE5]/30 p-3">
                  <div className="overline mb-1">STRONGEST</div>
                  {brief.stats.strongest.map((s) => (
                    <div key={s.key} className="text-[12px] flex justify-between"><span>{s.name}</span><b>{s.score}</b></div>
                  ))}
                </div>
                <div className="border border-[#EE2737]/30 bg-[#FEE2E2]/30 p-3">
                  <div className="overline mb-1 !text-[#EE2737]">CRITICAL GAPS</div>
                  {brief.stats.weakest.map((s) => (
                    <div key={s.key} className="text-[12px] flex justify-between"><span>{s.name}</span><b>{s.score}</b></div>
                  ))}
                </div>
                <div className="border border-[#E5E7EB] p-3">
                  <div className="overline mb-1">REGION</div>
                  {brief.stats.by_region.map((r) => (
                    <div key={r.region} className="text-[12px] flex justify-between"><span>{r.region}</span><b>{r.avg}</b></div>
                  ))}
                </div>
              </div>
              <div className="mt-5 text-[11px] text-[#878787] flex justify-between">
                <span>Generated {(brief.generated_at || "").slice(0, 16).replace("T", " ")}</span>
                <button onClick={() => { navigator.clipboard.writeText(brief.brief); }} className="text-[#EE2737] font-semibold hover:underline" data-testid="copy-brief">Copy to clipboard</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-[#E5E7EB] border border-[#E5E7EB]" data-testid="kpi-strip">
        <KPI label="Participants" value={k.total_participants} icon={<Users size={16} />} />
        <KPI label="Assignments" value={k.total_assignments} icon={<Layers size={16} />} />
        <KPI label="Submitted" value={k.submitted} icon={<CheckCircle2 size={16} />} />
        <KPI label="Scored" value={k.scored} icon={<BarChart3 size={16} />} accent />
        <KPI label="Completion" value={`${k.completion_pct}%`} icon={<Minus size={16} />} />
      </div>

      {/* Results Release Control — Feb 2026 */}
      {!isClient && <ResultsReleaseCard />}


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Org heatmap */}
        <div className="card p-6 lg:col-span-2" data-testid="org-heatmap-card">
          <div className="overline mb-1">ORG COMPETENCY HEATMAP</div>
          <h3 className="font-heading font-bold text-xl mb-6">Average proficiency · all scored participants</h3>
          <div className="space-y-2">
            {data.org_heatmap.map((c) => (
              <div key={c.key} className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-4 lg:col-span-3">
                  <div className="text-[13px] font-semibold">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#878787] font-semibold">{c.type}</div>
                </div>
                <div className="col-span-7 lg:col-span-8 flex gap-0.5">
                  {[1,2,3,4,5].map((lvl) => {
                    const filled = c.avg_score >= lvl - 0.5;
                    return (
                      <div key={lvl}
                        className={`flex-1 h-6 border ${filled ? "bg-[#EE2737] border-[#EE2737]" : "bg-white border-[#E5E7EB]"}`}
                      />
                    );
                  })}
                </div>
                <div className="col-span-1 text-right font-heading font-black text-base">{c.avg_score || "—"}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bench strength */}
        <div className="card p-6" data-testid="bench-strength-card">
          <div className="overline mb-1">BENCH STRENGTH</div>
          <h3 className="font-heading font-bold text-xl mb-6">Distribution by proficiency band</h3>
          <div className="space-y-3">
            {Object.entries(data.bench_strength).map(([label, count]) => {
              const total = Object.values(data.bench_strength).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={label}>
                  <div className="flex justify-between text-[13px] mb-1.5">
                    <span className="font-semibold">{label}</span>
                    <span className="font-heading font-black">{count} <span className="text-[#878787] font-normal text-xs">({pct}%)</span></span>
                  </div>
                  <div className="progress-bar"><div style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* By region */}
      {data.by_region.length > 0 && (
        <div className="card p-6">
          <div className="overline mb-1">REGION CUT</div>
          <h3 className="font-heading font-bold text-xl mb-6">Average competency score by region</h3>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Region</th>
                  {data.org_heatmap.map((c) => <th key={c.key}>{c.name.split(" ")[0]}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.by_region.map((row) => (
                  <tr key={row.region}>
                    <td className="font-semibold">{row.region}</td>
                    {data.org_heatmap.map((c) => (
                      <td key={c.key}><HeatCell value={row[c.key]} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// HeatCell + KPI moved to admin/_shared.jsx (see import above).

// Cycles moved to admin/CyclesTab.jsx.

// ---------------- People ----------------
// People (Users) moved to admin/UsersTab.jsx.

// PerformanceOverlayCard, LongitudinalTrendCard, IntegrityBanner moved to
// admin/ResultsTab.jsx (Feb 2026 modularisation phase 2 cleanup — they were
// only ever rendered inside the Sales Cert report which now lives there).

// Content Studio + all its dependent modals moved to admin/ContentTab.jsx.


// ---------------- Disputes (AI Role-Play Override) ----------------
function Disputes() {
  const [sessions, setSessions] = useState(null);
  const [filter, setFilter] = useState("all");
  const [err, setErr] = useState("");

  const load = () => {
    const q = filter === "scored" ? "?status=scored" : filter === "overridden" ? "?has_override=true" : "";
    api.get(`/admin/roleplay/sessions${q}`).then((r) => setSessions(r.data)).catch((e) => setErr(formatApiError(e)));
  };
  useEffect(() => { load(); }, [filter]);

  if (err) return <Err msg={err} />;
  if (!sessions) return <Loading />;

  return (
    <div className="space-y-6 fade-in">
      <PageHeader
        eyebrow="AI DISPUTE RESOLUTION"
        title="Role-play review queue"
        subtitle="Mercury consultants can override AI BARS scores per role-play exercise when a transcript-anchored manual review disagrees. Overrides recompute the participant's composite result automatically."
      />

      <div className="flex gap-2 flex-wrap" data-testid="dispute-filters">
        {[{ k: "all", n: "All sessions" }, { k: "scored", n: "AI-scored" }, { k: "overridden", n: "Overridden" }].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)}
            className={`px-3 py-1.5 text-[12px] font-semibold uppercase tracking-wider rounded-sm ${
              filter === f.k ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#E5E7EB] text-[#525252]"
            }`}
            data-testid={`dispute-filter-${f.k}`}
          >{f.n}</button>
        ))}
      </div>

      <div className="card overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr><th>Participant</th><th>Exercise</th><th>Lang</th><th>Status</th><th>AI score (avg)</th><th>Override</th><th></th></tr>
          </thead>
          <tbody>
            {sessions.length === 0 && <tr><td colSpan="7" className="text-center text-[#878787] py-10 text-sm">No sessions match this filter.</td></tr>}
            {sessions.map((s) => {
              const aiScores = s.scores?.scores || {};
              const aiAvg = Object.values(aiScores).length ? (Object.values(aiScores).reduce((a, b) => a + b, 0) / Object.values(aiScores).length).toFixed(2) : "—";
              const override = s.consultant_override;
              return (
                <tr key={s.id} data-testid={`dispute-row-${s.id}`}>
                  <td className="font-semibold">{s.participant_name}</td>
                  <td className="text-[13px]">{s.exercise_name}</td>
                  <td><span className="badge badge-neutral">{(s.language || "en-IN").split("-")[0].toUpperCase()}</span></td>
                  <td>
                    <span className={`badge ${s.status === "scored" ? "badge-success" : s.status === "active" ? "badge-warning" : "badge-neutral"}`}>{s.status}</span>
                  </td>
                  <td className="font-mono text-sm">{aiAvg}</td>
                  <td>
                    {override ? <span className="badge badge-info">Overridden by {override.reviewer_name}</span> : <span className="text-[#878787] text-xs">—</span>}
                  </td>
                  <td>
                    <Link to={`/admin/disputes/${s.id}`} className="text-[#EE2737] font-semibold text-sm hover:underline" data-testid={`dispute-review-${s.id}`}>Review →</Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DisputeDetail() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [scores, setScores] = useState({});
  const [summary, setSummary] = useState("");
  const [notes, setNotes] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/admin/roleplay/sessions/${sessionId}`);
      setS(r.data);
      const initial = r.data.consultant_override?.scores || r.data.scores?.scores || {};
      setScores(initial);
      setSummary(r.data.consultant_override?.overall_summary || r.data.scores?.overall_summary || "");
      setNotes(r.data.consultant_override?.notes || "");
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [sessionId]);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await api.post(`/admin/roleplay/sessions/${sessionId}/override`, {
        scores, overall_summary: summary, notes,
      });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const clearOverride = async () => {
    if (!window.confirm("Remove this override and revert to the AI's score?")) return;
    setSaving(true);
    try { await api.delete(`/admin/roleplay/sessions/${sessionId}/override`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (err) return <Err msg={err} />;
  if (!s) return <Loading />;

  const aiScores = s.scores?.scores || {};
  const aiJusts = s.scores?.justifications || {};
  const override = s.consultant_override;
  const competencies = s.competencies || Object.keys({ ...aiScores, ...scores });

  return (
    <div className="space-y-8 fade-in">
      <button onClick={() => navigate("/admin/disputes")} className="btn btn-ghost !px-2" data-testid="back-to-disputes">← Back to review queue</button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card p-6">
          <div className="overline mb-2 !text-[#EE2737]">DISPUTE REVIEW · ROLE-PLAY TRANSCRIPT</div>
          <h2 className="font-heading font-black text-2xl tracking-tight mb-1">{s.participant_name}</h2>
          <div className="text-sm text-[#525252]">{s.exercise_name} · {s.messages?.length || 0} messages · Language: {s.language || "en-IN"}</div>

          <div className="mt-5 max-h-[55vh] overflow-y-auto space-y-3 pr-2" data-testid="dispute-transcript">
            {(s.messages || []).map((m, i) => (
              <div key={i} className={`px-4 py-3 ${m.role === "user" ? "bg-[#F8F9FA] border-l-2 border-[#0A0A0A]" : "bg-[#FEF3C7] border-l-2 border-[#D97706]"}`}>
                <div className="overline mb-1 !text-[10px]">{m.role === "user" ? "PARTICIPANT" : "AI · " + s.exercise_name.split("—")[0]}</div>
                <div className="text-[13px] text-[#0A0A0A] leading-relaxed">{m.content}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card p-6">
          <div className="overline mb-3">CONSULTANT OVERRIDE</div>
          <div className="space-y-3 mb-5">
            {competencies.map((k) => (
              <div key={k} className="border border-[#E5E7EB] p-3 bg-white" data-testid={`override-${k}`}>
                <div className="text-[11px] uppercase tracking-wider font-bold text-[#525252] mb-1">{k.replace(/_/g, " ")}</div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] text-[#878787] uppercase tracking-wider">AI:</span>
                  <span className="font-mono text-sm font-bold text-[#525252]">{aiScores[k] ?? "—"}</span>
                  <span className="text-[10px] text-[#878787] uppercase tracking-wider ml-3">Override:</span>
                  <select
                    className="input !py-1 !px-2 !text-sm w-20"
                    value={scores[k] ?? ""}
                    onChange={(e) => setScores({ ...scores, [k]: parseInt(e.target.value, 10) })}
                    data-testid={`override-select-${k}`}
                  >
                    <option value="">—</option>
                    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                {aiJusts[k] && <div className="text-[11px] text-[#878787] italic leading-relaxed">AI: "{aiJusts[k]}"</div>}
              </div>
            ))}
          </div>

          <label className="overline mb-1 block">Reviewer overall summary</label>
          <textarea className="input font-body mb-3" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="One-paragraph rationale for the override…" data-testid="override-summary" />

          <label className="overline mb-1 block">Internal notes (optional)</label>
          <textarea className="input font-body mb-4" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Audit trail for Mercury team…" data-testid="override-notes" />

          {override && (
            <div className="bg-[#FEF3C7] border-l-2 border-[#D97706] px-3 py-2 text-[11px] text-[#92400E] mb-3 leading-relaxed">
              Existing override by <b>{override.reviewer_name}</b> on {(override.reviewed_at || "").slice(0, 10)}.
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="btn btn-primary flex-1" data-testid="save-override">
              <Save size={14} /> {saving ? "Saving…" : override ? "Update override" : "Apply override"}
            </button>
            {override && (
              <button onClick={clearOverride} disabled={saving} className="btn btn-secondary" data-testid="clear-override">
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Performance (Sales Metrics Overlay) ----------------
function Performance() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [period, setPeriod] = useState("Q1 2026");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/performance").then((r) => setRows(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("period", period);
      await api.post("/admin/performance/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (err) return <Err msg={err} />;
  if (!rows) return <Loading />;

  return (
    <div className="space-y-6 fade-in">
      <PageHeader
        eyebrow="SALES PERFORMANCE OVERLAY"
        title="Revenue, pipeline & forecast data"
        subtitle="Upload per-rep monthly/quarterly sales metrics — they overlay onto each participant's competency report so Mercury and VI Sales Heads can correlate capability against business outcomes."
      />

      <div className="card p-5 flex flex-wrap gap-3 items-end">
        <div>
          <label className="overline mb-1 block">Period</label>
          <input className="input !w-40" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Q1 2026" data-testid="perf-period" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <label className="overline mb-1 block">CSV — columns: email, revenue_actual, revenue_target, pipeline, forecast_accuracy</label>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            onChange={(e) => upload(e.target.files?.[0])}
            disabled={uploading}
            className="block w-full text-sm text-[#525252] file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#0A0A0A] file:text-white file:text-xs file:font-bold file:uppercase file:tracking-wider hover:file:bg-[#EE2737]"
            data-testid="perf-upload"
          />
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr><th>Rep email</th><th>Period</th><th>Actual ₹Cr</th><th>Target ₹Cr</th><th>Achievement %</th><th>Pipeline ₹Cr</th><th>Forecast acc %</th><th>Updated</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center text-[#878787] py-10 text-sm">No data uploaded yet. Upload a CSV above to seed.</td></tr>}
            {rows.map((r, i) => {
              const ach = r.revenue_target ? (r.revenue_actual / r.revenue_target * 100) : null;
              return (
                <tr key={i} data-testid={`perf-row-${r.email}-${r.period}`}>
                  <td className="font-semibold">{r.email}</td>
                  <td>{r.period}</td>
                  <td>{r.revenue_actual ?? "—"}</td>
                  <td>{r.revenue_target ?? "—"}</td>
                  <td>
                    {ach !== null ? (
                      <span className={`badge ${ach >= 100 ? "badge-success" : ach >= 80 ? "badge-warning" : "badge-red"}`}>{ach.toFixed(0)}%</span>
                    ) : "—"}
                  </td>
                  <td>{r.pipeline ?? "—"}</td>
                  <td>{r.forecast_accuracy ?? "—"}</td>
                  <td className="text-[11px] text-[#878787]">{(r.updated_at || "").slice(0, 10)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Emails + DailyDigestPanel moved to admin/EmailsTab.jsx.


function PageHeader(props) { return <SharedPageHeader {...props} />; }
function KPI(props) { return <SharedKPI {...props} />; }
function HeatCell(props) { return <SharedHeatCell {...props} />; }

// ---------------- Demo Panel (one-click reset of demo candidate) ----------------
function DemoPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [working, setWorking] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [lastReset, setLastReset] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get("/admin/demo/candidate")
    .then((r) => setStatus(r.data))
    .catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const doReset = async () => {
    if (!window.confirm("Reset the demo candidate? This will wipe Aanya Sharma's prior submission, role-play, and report, and give her a fresh PENDING assignment.")) return;
    setWorking(true); setErr("");
    try {
      const r = await api.post("/admin/demo/reset-candidate");
      setLastReset(r.data);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  const doSeedCohort = async () => {
    if (!window.confirm("Re-seed the entire demo cohort? Use this once after a fresh deployment, or to refresh the integrity scenarios. Idempotent — safe to re-run.")) return;
    setSeeding(true); setErr("");
    try {
      const r = await api.post("/admin/demo/seed-cohort");
      setSeedResult(r.data);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSeeding(false);
    }
  };

  if (err && !status) return <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-3" data-testid="demo-panel-error">{err}</div>;
  if (!status) return <div className="text-center text-[#878787] py-20 text-sm">Loading…</div>;

  const cand = status.candidate || {};
  const isFreshlyPending = status.has_pending && (status.latest_status === "pending") && (status.assignments?.length === 1);

  return (
    <div className="space-y-8 fade-in" data-testid="demo-panel">
      <PageHeader
        eyebrow="DEMO WORKSPACE"
        title="Experience the participant flow"
        subtitle="Hand this login to anyone in your team — or use it yourself — to walk through the exact journey a Vodafone Idea sales rep will go through, end-to-end. When the assessment is taken, click 'Reset' to start fresh."
      />

      {/* Engagement tracker — Mercury-only insight into how Avni (or any evaluator) is exploring the platform */}
      {isAdmin && <EngagementTracker />}

      {/* Login credentials card */}
      <div className="card p-6">
        <div className="overline mb-3 !text-[#EE2737]">DEMO CANDIDATE LOGIN</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <CredField label="Name" value={cand.name || "Aanya Sharma (Demo)"} />
          <CredField label="Region · Level" value={`${cand.region || "North"} · ${cand.role_level || "L1"}`} />
          <CredField label="Login email" value={cand.email || "demo.candidate@vi.com"} copyable />
          <CredField label="Password" value="Demo@COMPASS2026" copyable />
        </div>
        <div className="bg-[#F8F9FA] border-l-4 border-[#0A0A0A] p-3 text-[12.5px] leading-relaxed text-[#525252]">
          <b className="text-[#0A0A0A]">How to use this:</b> Open this URL in a different browser (or an incognito window so it doesn't overwrite your own login) → log in with the credentials above → click <em>"Start digital assessment"</em>. The pre-test ID verification, webcam proctoring, fullscreen lock, AI role-play, and personal report all come standard — exactly what each of your sales reps will experience.
        </div>
      </div>

      {/* Current status + reset */}
      <div className="card p-6">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <div className="overline">CURRENT STATE</div>
            <h3 className="font-heading font-bold text-xl mt-1">Aanya's assessment is{" "}
              <span className={status.has_pending ? "text-[#008A3D]" : "text-[#EE2737]"}>
                {status.has_pending ? "ready to take" : "already submitted"}
              </span>
            </h3>
            <p className="text-[12.5px] text-[#525252] mt-1">
              {isFreshlyPending
                ? "Clean slate — no prior attempt on file."
                : status.has_pending
                  ? `${status.assignments.length} assignment(s) on file. The pending one will be picked up first.`
                  : "Aanya has already taken the assessment. Reset to give a fresh PENDING attempt to the next viewer."}
            </p>
          </div>
          <button
            onClick={doReset}
            disabled={working}
            className="btn btn-primary"
            data-testid="reset-demo-candidate"
          >
            <RotateCcw size={14} /> {working ? "Resetting…" : "Reset demo candidate"}
          </button>
        </div>

        <div className="flex gap-3 flex-wrap mt-2">
          <button
            onClick={() => { trackClick("take_it_yourself"); window.open("/login?demo=candidate", "_blank", "noopener,noreferrer"); }}
            className="btn btn-secondary"
            data-testid="take-it-yourself"
          >
            <Eye size={14} /> Take it yourself · opens in new tab
          </button>
          <span className="text-[11px] text-[#878787] self-center max-w-md leading-relaxed">
            Opens the participant login in a new tab with Aanya's credentials pre-filled. Click <em>Sign in</em> there to start the full AI-proctored journey. Your current session stays untouched.
          </span>
        </div>

        {/* Assignments list */}
        {status.assignments?.length > 0 && (
          <div className="mt-4">
            <div className="overline mb-2">ATTEMPT HISTORY</div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Assignment ID</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {status.assignments.map((a) => (
                  <tr key={a.id}>
                    <td className="font-mono text-[11px]">{a.id}</td>
                    <td>
                      <span className={`badge ${
                        a.status === "scored" ? "badge-success" :
                        a.status === "submitted" ? "badge-info" :
                        a.status === "pending" ? "badge-warning" : "badge-neutral"
                      }`}>{a.status}</span>
                    </td>
                    <td className="text-[11px] text-[#878787]">{new Date(a.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {err && <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-3 py-2 mt-4">{err}</div>}
        {lastReset && (
          <div className="bg-[#D1FAE5] border-l-4 border-[#10B981] text-[#065F46] px-4 py-3 mt-4 text-sm" data-testid="reset-success">
            ✔ Wiped {lastReset.wiped_count} prior attempt(s). Fresh assignment ready in <b>{lastReset.cycle_name}</b>.
          </div>
        )}
      </div>

      {/* Re-seed cohort — admin only, used right after a fresh deploy */}
      {isAdmin && (
        <div className="card p-6 border-l-4 !border-[#0A0A0A]" data-testid="reseed-cohort-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="overline">POST-DEPLOY</div>
              <h3 className="font-heading font-bold text-xl mt-1 mb-1">Re-seed the entire demo cohort</h3>
              <p className="text-[12.5px] text-[#525252] leading-relaxed max-w-2xl">
                Use this <b>once</b> immediately after a fresh production deployment (or whenever you want to refresh the integrity scenarios). It rebuilds Avni's account, the 33-person cohort, Q4 2025 + Q1 2026 cycles, scored results, performance overlay, and the four AI-proctoring scenarios. Idempotent — safe to re-run.
              </p>
            </div>
            <button onClick={doSeedCohort} disabled={seeding} className="btn btn-secondary" style={{ background: "#0A0A0A", color: "white", borderColor: "#0A0A0A" }} data-testid="reseed-cohort">
              <Sparkles size={14} /> {seeding ? "Seeding…" : "Re-seed cohort"}
            </button>
          </div>
          {seedResult && (
            <div className="bg-[#D1FAE5] border-l-4 border-[#10B981] text-[#065F46] px-4 py-3 mt-4 text-sm" data-testid="seed-success">
              <div className="font-bold mb-1">✔ Cohort seeded successfully</div>
              <div className="text-[12px]">
                {seedResult.total_participants} participants · {seedResult.cycles.join(", ")} · {seedResult.integrity_scenarios_seeded.length} integrity scenarios.
              </div>
            </div>
          )}
        </div>
      )}

      {/* What Aanya will go through */}
      <div className="card p-6">
        <div className="overline mb-1 !text-[#EE2737]">WALK-THROUGH PREVIEW</div>
        <h3 className="font-heading font-bold text-xl mb-4">What Aanya experiences end-to-end</h3>
        <ol className="space-y-3 text-[13px] text-[#525252] leading-relaxed">
          <li className="flex gap-3"><span className="font-heading font-black text-[#EE2737] flex-shrink-0">01</span><span><b className="text-[#0A0A0A]">DPDP consent</b> — clear notice on what the AI proctor monitors (webcam every 15s, mic noise level, tab/paste/fullscreen).</span></li>
          <li className="flex gap-3"><span className="font-heading font-black text-[#EE2737] flex-shrink-0">02</span><span><b className="text-[#0A0A0A]">Identity verification</b> — live selfie + upload a government ID (JPEG, PNG, PDF). AI vision compares both in ~4 seconds.</span></li>
          <li className="flex gap-3"><span className="font-heading font-black text-[#EE2737] flex-shrink-0">03</span><span><b className="text-[#0A0A0A]">Phase 1 · Digital Assessment</b> — SJTs, MCQs, and open responses in fullscreen with live proctoring. Each tab switch / paste / fullscreen exit triggers a warning. Three warnings = auto-submit.</span></li>
          <li className="flex gap-3"><span className="font-heading font-black text-[#EE2737] flex-shrink-0">04</span><span><b className="text-[#0A0A0A]">Phase 2 · AI Role-Play</b> — an AI persona plays a B2B customer (CIO, CFO, channel partner) in text + voice, with native support for major Indian languages.</span></li>
          <li className="flex gap-3"><span className="font-heading font-black text-[#EE2737] flex-shrink-0">05</span><span><b className="text-[#0A0A0A]">Personal report</b> — radar of 7 competencies, BARS breakdown, AI justifications, downloadable PDF with Devanagari support, integrity badge.</span></li>
        </ol>
      </div>
    </div>
  );
}

function CredField({ label, value, copyable }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="border border-[#E5E7EB] p-3 bg-[#F8F9FA]">
      <div className="overline mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code className="text-[13px] font-bold flex-1 truncate">{value}</code>
        {copyable && (
          <button
            onClick={copy}
            className="text-[10px] text-[#525252] hover:text-[#EE2737] uppercase tracking-wider font-bold"
            data-testid={`copy-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {copied ? "✓ Copied" : "Copy"}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------- Engagement Tracker (Mercury-only) ----------------
function EngagementTracker() {
  const [email, setEmail] = useState("avni.malaviya@vodafoneidea.com");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showAllEvents, setShowAllEvents] = useState(false);

  const load = async (target) => {
    setLoading(true); setErr(""); setData(null);
    try {
      const r = await api.get(`/admin/engagement/${encodeURIComponent(target || email)}`);
      setData(r.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(email); /* eslint-disable-next-line */ }, []);

  const [wiping, setWiping] = useState(false);
  const wipe = async () => {
    // Always refetch first so the user wipes the LATEST state, not their cached view.
    setWiping(true); setErr("");
    try {
      const fresh = await api.get(`/admin/engagement/${encodeURIComponent(email)}`);
      const count = fresh.data?.summary?.events_total || 0;
      if (count === 0) {
        window.alert(`No activity logged for ${email} yet. Nothing to wipe.`);
        setData(fresh.data);
        return;
      }
      if (!window.confirm(`Wipe the activity log for ${fresh.data.user.name || email}? This will permanently delete ${count} event(s). Use this when you want a clean engagement baseline (e.g. before re-sharing the demo).`)) return;
      const r = await api.delete(`/admin/engagement/${encodeURIComponent(email)}`);
      await load(email);
      window.alert(`Wiped ${r.data.deleted} event(s). Engagement is back to 0/100.`);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setWiping(false); }
  };

  const verdictTone = (verdict) => ({
    "Highly engaged": { bg: "#D1FAE5", text: "#065F46", border: "#10B981" },
    "Engaged": { bg: "#DBEAFE", text: "#1E3A8A", border: "#2563EB" },
    "Light touch": { bg: "#FEF3C7", text: "#78350F", border: "#D97706" },
    "Logged in but barely explored": { bg: "#FEE2E2", text: "#7F1D1D", border: "#DC2626" },
  }[verdict] || { bg: "#F8F9FA", text: "#525252", border: "#E5E7EB" });

  const sortedRoutes = data ? Object.entries(data.route_counts).sort((a, b) => b[1] - a[1]) : [];
  const sortedClicks = data ? Object.entries(data.click_counts).sort((a, b) => b[1] - a[1]) : [];

  return (
    <div className="card p-6 border-l-4 !border-[#EE2737]" data-testid="engagement-tracker">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="overline !text-[#EE2737]">MERCURY-ONLY · EVALUATOR ENGAGEMENT</div>
          <h3 className="font-heading font-bold text-xl mt-1">How seriously is the client exploring?</h3>
          <p className="text-[12.5px] text-[#525252] max-w-2xl mt-1 leading-relaxed">
            Live activity log for any evaluator login — route visits, key button clicks, downloads, AI feature triggers. Visible to Mercury only. Use this to gauge depth of interest before the next steering call.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-2 flex-wrap">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input flex-1 min-w-[280px]"
          placeholder="avni.malaviya@vodafoneidea.com"
          data-testid="engagement-email"
        />
        <button onClick={() => load()} disabled={loading} className="btn btn-secondary" data-testid="engagement-load">
          {loading ? "Loading…" : "Load activity"}
        </button>
        <button
          onClick={wipe}
          disabled={wiping}
          className="btn btn-secondary"
          style={{ borderColor: "#EE2737", color: "#EE2737" }}
          data-testid="engagement-wipe"
          title="Permanently delete this evaluator's activity log. Useful before re-sharing the demo for a clean engagement baseline."
        >
          <Trash2 size={13} /> {wiping ? "Wiping…" : "Wipe activity log"}
        </button>
      </div>
      <div className="text-[11px] text-[#878787] mb-4">
        Tip: enter any user email (e.g. <code>demo.candidate@vi.com</code>) and click <em>Load activity</em>. The wipe button permanently deletes all logged events for that user — use it before handing the demo to a new evaluator so the engagement baseline is clean.
      </div>

      {err && <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-3 py-2 mb-3">{err}</div>}

      {data && (
        <>
          {/* Headline summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="engagement-summary">
            <KpiTile label="Engagement" value={`${data.summary.engagement_score}/100`} sub={data.summary.verdict} tone={verdictTone(data.summary.verdict)} />
            <KpiTile label="Sessions" value={data.summary.sessions} sub={`over ${data.summary.total_minutes} min`} />
            <KpiTile label="Routes visited" value={data.summary.unique_routes} sub={`${data.summary.events_total} events`} />
            <KpiTile label="Buttons clicked" value={data.summary.unique_clicks} sub="distinct" />
            <KpiTile
              label="Last seen"
              value={data.summary.last_seen ? new Date(data.summary.last_seen).toLocaleString().split(",")[0] : "—"}
              sub={data.summary.last_seen ? new Date(data.summary.last_seen).toLocaleTimeString() : ""}
            />
          </div>

          {/* Detail grids */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="overline mb-2">TOP ROUTES VISITED</div>
              {sortedRoutes.length === 0 && <p className="text-[12px] text-[#878787]">No route data yet.</p>}
              <div className="space-y-1">
                {sortedRoutes.slice(0, 10).map(([route, n]) => (
                  <div key={route} className="flex justify-between text-[12.5px] py-1 px-2 bg-[#F8F9FA] border-l-2 border-[#0A0A0A]">
                    <span className="font-mono truncate">{route || "(unknown)"}</span>
                    <b>{n}</b>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="overline mb-2">KEY ACTIONS CLICKED</div>
              {sortedClicks.length === 0 && <p className="text-[12px] text-[#878787]">No tracked actions yet.</p>}
              <div className="space-y-1">
                {sortedClicks.slice(0, 10).map(([label, n]) => (
                  <div key={label} className="flex justify-between text-[12.5px] py-1 px-2 bg-[#F8F9FA] border-l-2 border-[#EE2737]">
                    <span>{label.replace(/_/g, " ")}</span>
                    <b>{n}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent event timeline */}
          <details open={showAllEvents} onToggle={(e) => setShowAllEvents(e.target.open)} className="bg-[#F8F9FA] border border-[#E5E7EB] p-3">
            <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-[#525252]">
              Recent activity ({data.events.length})
            </summary>
            <div className="mt-3 max-h-72 overflow-y-auto">
              <table className="data-table">
                <thead>
                  <tr><th>When</th><th>Action</th><th>Route / Detail</th></tr>
                </thead>
                <tbody>
                  {data.events.map((e) => (
                    <tr key={e.id}>
                      <td className="text-[11px] font-mono whitespace-nowrap">{new Date(e.occurred_at).toLocaleString()}</td>
                      <td><span className="badge badge-neutral">{e.action}</span></td>
                      <td className="text-[11px] text-[#525252]">
                        {e.action === "route" ? <code>{e.route}</code> : (e.detail?.label || JSON.stringify(e.detail || {}))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </div>
  );
}

function KpiTile(props) { return SharedKpiTile(props); }

function Loading() { return <SharedLoading />; }
function Err(props) { return <SharedErr {...props} />; }

// ---------------- Admin System (Content Import Wizard + Raw Reset) ----------------
function SystemReset() {
  const [view, setView] = useState("wizard"); // wizard | advanced | pilot
  return (
    <div className="max-w-5xl space-y-6" data-testid="admin-system-page">
      <div>
        <div className="overline mb-2">SYSTEM · CONTENT LIFECYCLE</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter">Content Import &amp; Reset</h1>
        <p className="text-sm text-[#525252] mt-2 leading-relaxed max-w-2xl">
          Swap the seed / test data for VI&apos;s authored bundle in three guided steps, or use the raw reset controls
          for surgical wipes. Admin and Client-Lead accounts are always preserved.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 border-b border-[#E5E7EB]" data-testid="admin-system-tabs">
        <button
          onClick={() => setView("wizard")}
          className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px ${
            view === "wizard" ? "border-[#EE2737] text-[#0A0A0A]" : "border-transparent text-[#878787]"
          }`}
          data-testid="admin-system-tab-wizard"
        >
          Content Import Wizard
        </button>
        <button
          onClick={() => setView("pilot")}
          className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px ${
            view === "pilot" ? "border-[#EE2737] text-[#0A0A0A]" : "border-transparent text-[#878787]"
          }`}
          data-testid="admin-system-tab-pilot"
        >
          Pilot Controls
        </button>
        <button
          onClick={() => setView("advanced")}
          className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px ${
            view === "advanced" ? "border-[#EE2737] text-[#0A0A0A]" : "border-transparent text-[#878787]"
          }`}
          data-testid="admin-system-tab-advanced"
        >
          Advanced Reset
        </button>
      </div>

      {view === "wizard" ? <ContentImportWizard /> : view === "pilot" ? <PilotControlsPanel /> : <RawResetPanel />}
    </div>
  );
}

function PilotControlsPanel() {
  const [vis, setVis] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    api.get("/settings/ui-visibility").then((r) => setVis(r.data));
  }, []);

  const toggleIntegrity = async (v) => {
    setSaving(true); setMsg("");
    try {
      const r = await api.patch("/admin/settings/ui-visibility", { client_lead_integrity: v });
      setVis(r.data);
      setMsg(`Integrity tab for Client Lead is now ${v ? "VISIBLE" : "HIDDEN"}.`);
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const runResetPilot = async () => {
    setResetting(true); setResetResult(null);
    try {
      const r = await api.post("/admin/salescert/reset-pilot", { confirm: resetPhrase });
      setResetResult({ ok: true, ...r.data });
      setResetPhrase("");
    } catch (e) { setResetResult({ ok: false, error: formatApiError(e) }); }
    finally { setResetting(false); }
  };

  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const runPurgeLegacy = async () => {
    if (!window.confirm("Delete the 10 legacy 4D-framework demo users (arjun, sneha, karan, divya, suresh, neha + 4 managers/partners) and their cascading assignments/results? This is safe — the pilot testers, admin, Avni, and Aanya are untouched.")) return;
    setPurging(true); setPurgeResult(null);
    try {
      const r = await api.post("/admin/system/purge-legacy-users");
      setPurgeResult({ ok: true, ...r.data });
    } catch (e) { setPurgeResult({ ok: false, error: formatApiError(e) }); }
    finally { setPurging(false); }
  };

  const [nonPilotPreview, setNonPilotPreview] = useState(null);
  const [nonPilotBusy, setNonPilotBusy] = useState(false);
  const [strictPurgeResult, setStrictPurgeResult] = useState(null);
  const loadNonPilotPreview = async () => {
    setNonPilotBusy(true); setNonPilotPreview(null); setStrictPurgeResult(null);
    try {
      const r = await api.get("/admin/system/non-pilot-users");
      setNonPilotPreview(r.data);
    } catch (e) { setStrictPurgeResult({ ok: false, error: formatApiError(e) }); }
    finally { setNonPilotBusy(false); }
  };
  const runStrictPurge = async () => {
    if (!nonPilotPreview) return;
    if (!window.confirm(`Delete ${nonPilotPreview.count} non-pilot users and every one of their assignments/results/submissions/attempts? This CANNOT be undone. Only these emails will remain: ${nonPilotPreview.allowlist.join(", ")}`)) return;
    setNonPilotBusy(true); setStrictPurgeResult(null);
    try {
      const r = await api.post("/admin/system/purge-non-pilot-users");
      setStrictPurgeResult({ ok: true, ...r.data });
      setNonPilotPreview(null);
    } catch (e) { setStrictPurgeResult({ ok: false, error: formatApiError(e) }); }
    finally { setNonPilotBusy(false); }
  };

  return (
    <div className="space-y-8 fade-in" data-testid="pilot-controls-panel">
      {/* Client Lead visibility toggles */}
      <div className="card p-6 space-y-4" data-testid="clientlead-visibility-card">
        <div>
          <h2 className="font-heading font-bold text-xl">Client Lead visibility</h2>
          <p className="text-xs text-[#525252] mt-1">Fine-tune which admin tabs Avni (and future client leads) can see. Overview, Cycles, People, Results, Performance, Emails are always on.</p>
        </div>
        {vis && (
          <div className="flex items-center justify-between p-4 bg-[#F5F3EE] border border-[#E5E7EB]">
            <div className="flex-1 pr-4">
              <div className="font-bold text-sm">Integrity tab</div>
              <div className="text-[11px] text-[#525252] mt-0.5">When ON, the client lead can see the AI-proctor flags and per-attempt integrity scores. Kept OFF by default because raw flags can feel accusatory before Mercury has triaged them.</div>
            </div>
            <button
              onClick={() => toggleIntegrity(!vis.client_lead_integrity)}
              disabled={saving}
              data-testid="toggle-clientlead-integrity"
              className={`relative w-14 h-7 rounded-full transition-colors ${vis.client_lead_integrity ? "bg-[#EE2737]" : "bg-[#E5E7EB]"}`}
            >
              <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${vis.client_lead_integrity ? "translate-x-7" : "translate-x-0.5"}`} />
            </button>
          </div>
        )}
        {msg && <div className="text-xs text-[#525252]">{msg}</div>}
      </div>

      {/* Pilot data reset */}
      <div className="card p-6 space-y-4 border-l-4 border-[#DC2626]" data-testid="pilot-reset-card">
        <div>
          <h2 className="font-heading font-bold text-xl">Reset all pilot results</h2>
          <p className="text-xs text-[#525252] mt-1 leading-relaxed">
            Wipes every participant&apos;s Sales Cert attempt data — MCQ submissions, sales pitches, roleplay sessions, certificates, ID verifications, proctor events. <strong>Participant accounts themselves are preserved</strong> and their programme invitations reset to &ldquo;invited&rdquo; so they can start fresh. Use this right before the real cohort goes live.
          </p>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest font-bold text-[#525252] mb-1">Type <code className="bg-[#F3F4F6] px-1.5 py-0.5 text-[10px]">RESET PILOT</code> to confirm</label>
          <input
            value={resetPhrase}
            onChange={(e) => setResetPhrase(e.target.value)}
            placeholder="RESET PILOT"
            className="input font-mono text-sm"
            data-testid="pilot-reset-phrase"
          />
        </div>
        <button
          onClick={runResetPilot}
          disabled={resetting || resetPhrase !== "RESET PILOT"}
          data-testid="pilot-reset-submit"
          className="btn !bg-[#DC2626] !text-white hover:!bg-[#991B1B] disabled:!bg-[#E5E7EB] disabled:!text-[#878787]"
        >
          {resetting ? "Wiping…" : "Wipe all pilot results"}
        </button>
        {resetResult && (
          <div className={`p-4 border-l-4 ${resetResult.ok ? "bg-[#E6F4EC] border-[#008A3D]" : "bg-[#FEE2E2] border-[#DC2626]"}`}>
            {resetResult.ok ? (
              <div className="text-sm">
                <div className="font-bold text-[#008A3D] mb-1">Reset complete</div>
                <div className="text-xs text-[#525252]">Participants preserved: <strong>{resetResult.participants_preserved}</strong></div>
                <div className="text-xs text-[#525252] mt-1">Wiped: {Object.entries(resetResult.wiped || {}).map(([k, v]) => `${k}=${v}`).join(" · ") || "nothing (all clean)"}</div>
              </div>
            ) : (
              <div className="text-xs text-[#991B1B]">{resetResult.error}</div>
            )}
          </div>
        )}
      </div>

      {/* Purge legacy 4D-framework seed users (one-shot post-deploy cleanup) */}
      <div className="card p-6 space-y-3" data-testid="purge-legacy-users-card">
        <div>
          <div className="overline mb-1">POST-DEPLOY · ONE-SHOT CLEANUP</div>
          <h3 className="font-heading font-bold text-lg">Purge Legacy Demo Users (allow-list)</h3>
          <p className="text-xs text-[#525252] leading-relaxed mt-1">
            Removes the 10 hardcoded 4D-framework seed users (Arjun Reddy, Sneha Pillai, Karan Malhotra, Divya Nair, Suresh Patil, Neha Joshi + 4 managers/channel partners) plus their cascading assignments, results, submissions, feedback, attempts, and integrity records. Scoped strictly to a hardcoded email allow-list — <strong>pilot testers, admin, Avni and Aanya are never touched.</strong> Idempotent · safe to run twice.
          </p>
        </div>
        <button
          onClick={runPurgeLegacy}
          disabled={purging}
          className="btn btn-secondary"
          data-testid="purge-legacy-users-btn"
        >
          {purging ? "Purging…" : "Purge Legacy Seed Users"}
        </button>
        {purgeResult && (
          <div className={`p-3 border-l-4 ${purgeResult.ok ? "border-[#059669] bg-[#F0FDF4]" : "border-[#DC2626] bg-[#FEF2F2]"}`}>
            {purgeResult.ok ? (
              <div className="text-xs text-[#065F46]">
                <div className="font-bold">Purge complete.</div>
                <div className="mt-1">Deleted emails: {(purgeResult.purged_emails || []).join(", ") || "(none — DB was already clean)"}</div>
                <div className="mt-1">Cascade wipes: {Object.entries(purgeResult.wiped || {}).map(([k, v]) => `${k}=${v}`).join(" · ") || "none"}</div>
              </div>
            ) : (
              <div className="text-xs text-[#991B1B]">{purgeResult.error}</div>
            )}
          </div>
        )}
      </div>

      {/* Stricter: purge EVERYTHING outside the sanctioned allow-list (use when
          archive-restored ghost users are still lingering after the legacy purge) */}
      <div className="card p-6 space-y-3 border-l-4 !border-[#DC2626]" data-testid="purge-nonpilot-users-card">
        <div>
          <div className="overline mb-1 !text-[#DC2626]">STRICTER · ALLOW-LIST ONLY</div>
          <h3 className="font-heading font-bold text-lg">Purge Non-Pilot Users</h3>
          <p className="text-xs text-[#525252] leading-relaxed mt-1">
            Nukes every user whose email is NOT in the sanctioned pilot allow-list, plus all of their cascading data. Use this if the older "Purge Legacy Seed Users" button didn&apos;t catch archive-restored ghost accounts. <strong>Always preview first</strong> — the preview shows the exact list before you commit.
          </p>
          <p className="text-xs text-[#525252] mt-2">
            Allow-list (permanent, cannot be widened by API): <code className="text-[10px] bg-[#F3F4F6] px-1 py-0.5">admin</code>, <code className="text-[10px] bg-[#F3F4F6] px-1 py-0.5">avni</code>, <code className="text-[10px] bg-[#F3F4F6] px-1 py-0.5">demo.candidate (Aanya)</code>, <code className="text-[10px] bg-[#F3F4F6] px-1 py-0.5">founder.test</code>, <code className="text-[10px] bg-[#F3F4F6] px-1 py-0.5">mercury.tester1-4</code>, <code className="text-[10px] bg-[#F3F4F6] px-1 py-0.5">vi.pilot1-5</code>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={loadNonPilotPreview}
            disabled={nonPilotBusy}
            className="btn btn-secondary"
            data-testid="preview-nonpilot-btn"
          >
            {nonPilotBusy && !nonPilotPreview ? "Loading…" : "1 · Preview Who Will Be Removed"}
          </button>
          {nonPilotPreview && nonPilotPreview.count > 0 && (
            <button
              onClick={runStrictPurge}
              disabled={nonPilotBusy}
              className="btn"
              style={{ background: "#DC2626", color: "white" }}
              data-testid="strict-purge-btn"
            >
              {nonPilotBusy ? "Purging…" : `2 · Delete ${nonPilotPreview.count} User${nonPilotPreview.count === 1 ? "" : "s"}`}
            </button>
          )}
        </div>
        {nonPilotPreview && (
          <div className="p-3 border-l-4 border-[#F59E0B] bg-[#FEF3C7] max-h-80 overflow-auto">
            <div className="text-xs font-bold text-[#92400E] mb-2">
              {nonPilotPreview.count === 0 ? "✅ Nothing to purge — allow-list matches the DB exactly." : `${nonPilotPreview.count} user(s) will be permanently deleted:`}
            </div>
            {nonPilotPreview.count > 0 && (
              <table className="w-full text-[11px]">
                <thead className="text-[10px] uppercase text-[#78350F]">
                  <tr><th className="text-left pb-1">Email</th><th className="text-left pb-1">Name</th><th className="text-left pb-1">Role</th><th className="text-left pb-1">Region</th></tr>
                </thead>
                <tbody>
                  {nonPilotPreview.users.map((u) => (
                    <tr key={u.id} className="border-t border-[#FDE68A]">
                      <td className="py-1 pr-2 font-mono">{u.email}</td>
                      <td className="py-1 pr-2">{u.name || "—"}</td>
                      <td className="py-1 pr-2">{u.role}{u.role_level ? ` · ${u.role_level}` : ""}</td>
                      <td className="py-1 pr-2">{u.region || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {strictPurgeResult && (
          <div className={`p-3 border-l-4 ${strictPurgeResult.ok ? "border-[#059669] bg-[#F0FDF4]" : "border-[#DC2626] bg-[#FEF2F2]"}`}>
            {strictPurgeResult.ok ? (
              <div className="text-xs text-[#065F46]">
                <div className="font-bold">Purge complete — {strictPurgeResult.purged_count} user{strictPurgeResult.purged_count === 1 ? "" : "s"} removed.</div>
                <div className="mt-1">Cascade wipes: {Object.entries(strictPurgeResult.wiped || {}).map(([k, v]) => `${k}=${v}`).join(" · ") || "none"}</div>
                <div className="mt-1 text-[10px]">Reload the People page to see the updated list.</div>
              </div>
            ) : (
              <div className="text-xs text-[#991B1B]">{strictPurgeResult.error}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const WizardStepCircle = ({ n, active, done }) => (
  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-heading font-black text-sm ${
    done ? "bg-[#059669] text-white" : active ? "bg-[#EE2737] text-white" : "bg-[#E5E7EB] text-[#878787]"
  }`}>{done ? <CheckCircle2 size={16} /> : n}</div>
);

// -------- 3-step Content Import Wizard --------
function ContentImportWizard() {
  const [step, setStep] = useState(1);
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [wipeResult, setWipeResult] = useState(null);
  const [confirmPhrase, setConfirmPhrase] = useState("");

  const loadCounts = () => api.get("/admin/system/counts").then((r) => setCounts(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { loadCounts(); }, []);

  const doWipe = async () => {
    if (!counts || confirmPhrase !== counts.confirmation_phrase) return;
    if (!window.confirm("This will permanently delete all authored content + attempts. Continue?")) return;
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/admin/system/reset", {
        confirm: confirmPhrase,
        wipe_attempts: true,
        wipe_content: true,
        wipe_participants: false,
      });
      setWipeResult(r.data);
      await loadCounts();
      setStep(3);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!counts) return <Loading />;

  const contentNonZero = Object.entries(counts.content).filter(([, v]) => v > 0);
  const totalContentItems = contentNonZero.reduce((s, [, v]) => s + v, 0);

  return (
    <div className="space-y-8" data-testid="content-import-wizard">
      {/* Stepper */}
      <div className="flex items-center gap-2 pb-4 border-b border-[#E5E7EB]">
        <WizardStepCircle n={1} active={step === 1} done={step > 1} />
        <div className="text-xs">
          <div className="font-bold text-[#0A0A0A]">Preview</div>
          <div className="text-[10px] uppercase tracking-widest text-[#878787]">What&apos;s currently in the system</div>
        </div>
        <div className="flex-1 h-px bg-[#E5E7EB] mx-2" />
        <WizardStepCircle n={2} active={step === 2} done={step > 2} />
        <div className="text-xs">
          <div className="font-bold text-[#0A0A0A]">Wipe test content</div>
          <div className="text-[10px] uppercase tracking-widest text-[#878787]">Clear the sandbox</div>
        </div>
        <div className="flex-1 h-px bg-[#E5E7EB] mx-2" />
        <WizardStepCircle n={3} active={step === 3} />
        <div className="text-xs">
          <div className="font-bold text-[#0A0A0A]">Upload real bundle</div>
          <div className="text-[10px] uppercase tracking-widest text-[#878787]">MCQ · Pitch · Role Play</div>
        </div>
      </div>

      {/* Step 1: Preview */}
      {step === 1 && (
        <div className="card p-6 space-y-4" data-testid="wizard-step-1">
          <div>
            <div className="overline mb-1">STEP 1 · PREVIEW CURRENT DATA</div>
            <h2 className="font-heading font-bold text-2xl">Here&apos;s what&apos;s in the system today</h2>
            <p className="text-xs text-[#525252] mt-1">Everything below is seed / test data. Wiping in Step 2 preserves admin + client-lead accounts and audit logs.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="overline mb-2 text-[#059669]">Authored content ({totalContentItems} items)</div>
              <div className="text-xs text-[#525252] space-y-1">
                {contentNonZero.map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-[#F5F5F5] py-1">
                    <span>{k.replace(/_/g, " ")}</span>
                    <b>{v}</b>
                  </div>
                ))}
                {contentNonZero.length === 0 && <div className="italic text-[#878787]">Content collections already empty.</div>}
              </div>
            </div>
            <div>
              <div className="overline mb-2 text-[#EE2737]">Candidate attempts (will be wiped)</div>
              <div className="text-xs text-[#525252] space-y-1">
                {Object.entries(counts.attempts).filter(([, v]) => v > 0).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-[#F5F5F5] py-1">
                    <span>{k.replace(/_/g, " ")}</span>
                    <b>{v}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="pt-4 flex items-center gap-3">
            <button onClick={() => setStep(2)} className="btn btn-primary text-xs" data-testid="wizard-next-1">
              I have VI&apos;s bundle · continue <ArrowUpRight size={14} />
            </button>
            <span className="text-[11px] text-[#878787]">
              Have the 3 XLSX files ready: Question Authoring Template + Sales Pitch Prompts + Role-Play Scenarios.
            </span>
          </div>
        </div>
      )}

      {/* Step 2: Wipe */}
      {step === 2 && (
        <div className="card p-6 space-y-4 border-2 border-[#EE2737]/30" data-testid="wizard-step-2">
          <div>
            <div className="overline mb-1 text-[#EE2737]">STEP 2 · WIPE TEST CONTENT</div>
            <h2 className="font-heading font-bold text-2xl">This step is irreversible</h2>
            <p className="text-xs text-[#525252] mt-1">
              Deletes {totalContentItems} authored items + all candidate attempts. Admin + Client-Lead accounts kept. Every wipe is logged to <code>system_audit_events</code>.
            </p>
          </div>
          <div className="bg-[#FEF2F2] p-4 border border-[#EE2737]">
            <p className="text-xs text-[#525252] mb-2">
              Type <code className="bg-white px-2 py-1 font-mono border border-[#EE2737]">{counts.confirmation_phrase}</code> to enable the wipe button.
            </p>
            <input
              type="text"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              placeholder="WIPE-ALL-DATA"
              className="input font-mono text-sm"
              data-testid="wizard-confirm-input"
            />
            {err && <div className="text-xs text-[#EE2737] mt-2 font-bold">{err}</div>}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setStep(1)} className="btn btn-secondary text-xs">
              Back
            </button>
            <button
              onClick={doWipe}
              disabled={busy || confirmPhrase !== counts.confirmation_phrase}
              className={`btn text-xs ${confirmPhrase === counts.confirmation_phrase && !busy ? "btn-primary" : "btn-secondary opacity-40"}`}
              data-testid="wizard-wipe"
            >
              <AlertTriangle size={14} /> {busy ? "Wiping…" : "Execute wipe"}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Upload */}
      {step === 3 && (
        <div className="card p-6 space-y-5" data-testid="wizard-step-3">
          <div>
            <div className="overline mb-1 text-[#059669]">STEP 3 · UPLOAD VI&apos;S AUTHORED BUNDLE</div>
            <h2 className="font-heading font-bold text-2xl">System is empty — ready to import</h2>
            {wipeResult && (
              <p className="text-xs text-[#059669] mt-1 flex items-center gap-2">
                <CheckCircle2 size={12} /> Wiped {Object.values(wipeResult.wiped).reduce((s, v) => s + v, 0)} records.
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <UploadTile
              icon={<BookOpen size={16} />}
              label="MCQ · SJT · Open Response · Case"
              href="/admin/content"
              cta="Open Content Studio"
              hint="Use the 'Bulk Upload' button on any tab. Accepts the 4-tab COMPASS XLSX template."
            />
            <UploadTile
              icon={<Mic size={16} />}
              label="Sales Pitch Prompts"
              href="/admin/content"
              cta="Bulk Upload"
              hint="Uses the 'Sales Pitch' tab of the same XLSX template."
            />
            <UploadTile
              icon={<MessageSquare size={16} />}
              label="AI Role Play Scenarios"
              href="/admin/content"
              cta="Bulk Upload"
              hint="Uses the 'Role Play' tab of the XLSX template."
            />
          </div>

          <div className="card p-4 bg-[#F8F9FA]">
            <div className="overline mb-2">POST-UPLOAD VERIFICATION</div>
            <p className="text-xs text-[#525252] mb-3">
              After uploading, return here to confirm the new item counts. You should see the numbers below climb from zero.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              {["mcq_questions", "salespitch_prompts", "roleplay_scenarios", "programs"].map((k) => (
                <div key={k} className="p-3 border border-[#E5E7EB] bg-white">
                  <div className="overline">{k.replace(/_/g, " ")}</div>
                  <div className="font-heading font-black text-2xl mt-1">{counts.content[k] || 0}</div>
                </div>
              ))}
            </div>
            <button onClick={loadCounts} className="btn btn-secondary text-xs mt-3" data-testid="wizard-refresh">
              <RotateCcw size={12} /> Refresh counts
            </button>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <a href="/admin/deliverables" className="btn btn-secondary text-xs">
              <Download size={12} /> Get XLSX templates
            </a>
            <button onClick={() => { setStep(1); setWipeResult(null); setConfirmPhrase(""); }} className="text-xs uppercase tracking-widest text-[#878787] hover:text-[#0A0A0A]" data-testid="wizard-restart">
              Start over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const UploadTile = ({ icon, label, href, cta, hint }) => (
  <div className="card p-4 flex flex-col gap-3">
    <div className="w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center">{icon}</div>
    <div className="flex-1">
      <div className="font-heading font-bold text-sm mb-1">{label}</div>
      <div className="text-[11px] text-[#878787] leading-relaxed">{hint}</div>
    </div>
    <Link to={href} className="btn btn-primary text-xs justify-center">
      {cta} <ArrowUpRight size={12} />
    </Link>
  </div>
);

// -------- Advanced (raw) reset panel — kept for surgical wipes --------
function RawResetPanel() {
  const [counts, setCounts] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [wipeContent, setWipeContent] = useState(false);
  const [wipeParticipants, setWipeParticipants] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get("/admin/system/counts").then((r) => setCounts(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const doReset = async () => {
    if (!counts) return;
    if (confirmPhrase !== counts.confirmation_phrase) {
      setErr(`Type "${counts.confirmation_phrase}" exactly to enable the reset.`);
      return;
    }
    if (!window.confirm(`This will permanently delete data. Continue?`)) return;
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/admin/system/reset", {
        confirm: confirmPhrase,
        wipe_attempts: true,
        wipe_content: wipeContent,
        wipe_participants: wipeParticipants,
      });
      setResult(r.data);
      setConfirmPhrase("");
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!counts) return <Loading />;

  const canReset = confirmPhrase === counts.confirmation_phrase && !busy;

  return (
    <div className="space-y-6" data-testid="admin-system-reset">
      {result && (
        <div className="card p-5 border-2 border-[#059669] bg-[#F0FDF4]" data-testid="system-reset-result">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={16} className="text-[#059669]" />
            <span className="font-heading font-bold">Reset completed</span>
          </div>
          <div className="text-xs text-[#065F46] space-y-1">
            {Object.entries(result.wiped).map(([k, v]) => (
              <div key={k}><b>{k}</b>: {v} records deleted</div>
            ))}
            {Object.keys(result.wiped).length === 0 && <div>Nothing to delete — already clean.</div>}
          </div>
        </div>
      )}

      <div className="card p-6 border-2 border-[#EE2737]/30">
        <div className="overline mb-4">RESET OPTIONS</div>
        <div className="space-y-3">
          <label className="flex items-start gap-3">
            <input type="checkbox" checked disabled className="mt-1 accent-[#EE2737]" />
            <div>
              <div className="text-sm font-bold">Wipe candidate attempts (always)</div>
              <div className="text-xs text-[#525252]">Deletes all submissions, sessions, assignments, invitations, and proctoring events.</div>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={wipeContent}
              onChange={(e) => setWipeContent(e.target.checked)}
              className="mt-1 accent-[#EE2737]"
              data-testid="system-reset-toggle-banks"
            />
            <div>
              <div className="text-sm font-bold">Wipe content banks</div>
              <div className="text-xs text-[#525252]">MCQs, scenarios, sales-pitch prompts, exercises, programmes.</div>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={wipeParticipants}
              onChange={(e) => setWipeParticipants(e.target.checked)}
              className="mt-1 accent-[#EE2737]"
              data-testid="system-reset-toggle-participants"
            />
            <div>
              <div className="text-sm font-bold">Wipe participant users</div>
              <div className="text-xs text-[#525252]">Deletes all candidate accounts. Admins and Client-Leads always preserved.</div>
            </div>
          </label>
        </div>
      </div>

      <div className="card p-6 bg-[#FEF2F2] border-2 border-[#EE2737]">
        <div className="overline mb-2 text-[#EE2737]">TYPE TO CONFIRM</div>
        <p className="text-xs text-[#525252] mb-3">
          Type <code className="bg-white px-2 py-1 font-mono border border-[#EE2737]">{counts.confirmation_phrase}</code> exactly to enable the button.
        </p>
        <input
          type="text"
          value={confirmPhrase}
          onChange={(e) => setConfirmPhrase(e.target.value)}
          placeholder="WIPE-ALL-DATA"
          className="input font-mono text-sm"
          data-testid="system-reset-confirm-input"
        />
        {err && <div className="text-xs text-[#EE2737] mt-2 font-bold">{err}</div>}
        <div className="mt-4 flex gap-3 items-center">
          <button
            onClick={doReset}
            disabled={!canReset}
            className={`btn ${canReset ? "btn-primary" : "btn-secondary opacity-40"} text-xs`}
            data-testid="system-reset-execute"
          >
            <AlertTriangle size={14} /> {busy ? "Wiping…" : "Execute reset"}
          </button>
          <span className="text-[11px] text-[#878787]">
            {canReset ? "Ready · this cannot be undone" : "Type the phrase above to enable"}
          </span>
        </div>
      </div>
    </div>
  );
}